import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotebookPen } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

type MemoResponse = { memo_body: string | null; updated_at: string | null };

type Props = {
  nodeId: string;
  /** 'YYYY-MM' (呼び出し元は periodStart.slice(0, 7) 等で渡す、AiPeriodCommentPanel と同じ規約) */
  targetMonth: string;
};

/**
 * v0.5⑤ (2026-09-08 モックフィードバック「メモ（原因・対策）」、
 * docs/design/v05-dashboard-consolidation-detail.md §2)。
 * その月・そのノードの振り返りメモを1件だけ保持する薄い編集パネル。
 */
export const DashboardNodeMemo = ({ nodeId, targetMonth }: Props) => {
  const qc = useQueryClient();
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canEdit = hasPermissionForFeatureAction('dashboard', 'update');
  const month = `${targetMonth}-01`;

  const { data } = useQuery({
    queryKey: ['dashboard-memo', nodeId, month],
    queryFn: () => apiFetch<MemoResponse>(`/dashboard/nodes/${nodeId}/memo?month=${month}`),
  });

  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setDraft(data?.memo_body ?? ''); }, [data?.memo_body]);

  const saveMut = useMutation({
    mutationFn: (memo_body: string) => apiFetch<MemoResponse>(`/dashboard/nodes/${nodeId}/memo?month=${month}`, {
      method: 'PUT',
      body: JSON.stringify({ memo_body }),
    }),
    onSuccess: (res) => {
      qc.setQueryData(['dashboard-memo', nodeId, month], res);
      setEditing(false);
      toast.success('メモを保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'メモの保存に失敗しました'),
  });

  // 編集権限が無いユーザーには、メモが空なら何も表示しない (空の枠だけ出す意味が無い)
  if (!canEdit && !data?.memo_body) return null;

  return (
    <div className="rounded-lg border border-surface-border bg-white px-4 py-3">
      <div className="flex items-center gap-1.5 text-[12px] font-extrabold text-text-sub mb-2">
        <NotebookPen size={14} className="text-brand-orange" aria-hidden /> メモ（原因・対策）
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            rows={3}
            className="w-full text-[13px] border border-surface-border rounded px-2 py-1.5 focus:outline-none focus-ring"
            placeholder="今月の振り返り・対応策を記入してください"
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => saveMut.mutate(draft)} disabled={saveMut.isPending}
              className="text-[12px] font-bold text-white bg-brand-red hover:bg-brand-red-hover disabled:opacity-50 rounded px-3 py-1.5 min-h-[36px]">
              {saveMut.isPending ? '保存中…' : '保存'}
            </button>
            <button type="button" onClick={() => { setDraft(data?.memo_body ?? ''); setEditing(false); }}
              className="text-[12px] font-bold text-text-sub hover:text-text-main rounded px-3 py-1.5 min-h-[36px]">
              キャンセル
            </button>
          </div>
        </div>
      ) : (
        <>
          {data?.memo_body
            ? <p className="text-[13px] text-text-body whitespace-pre-wrap">{data.memo_body}</p>
            : <p className="text-[13px] text-text-muted">まだメモがありません</p>}
          {canEdit && (
            <button type="button" onClick={() => setEditing(true)}
              className="mt-2 text-[11px] font-bold text-text-sub hover:text-text-main underline min-h-[36px]">
              {data?.memo_body ? '編集する' : 'メモを書く'}
            </button>
          )}
        </>
      )}
    </div>
  );
};
